import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	AlertBar,
	BusyIndicator,
	FloatingActionButton,
	FormattedText,
	SearchInputForm,
	SearchPlaceholder,
	TableView,
	TableViewNoResultConfig,
	ToolbarAction,
	ToolbarRegularAction,
	useConfirmationDialog
} from "client/Components";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useSelection,
	useSortHandler,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { contains } from "client/Data/searching";
import {
	DataSourceSortableState,
	DataSourceSortDirection
} from "client/Data/sorting";
import { localize } from "client_legacy/ndaw/culture";
import {
	deleteSharedSettings,
	IntegrationSharedSettingDeleteResult,
	IntegrationSharedSettingDeleteResultKind,
	IntegrationSharedSettingOptionModel,
	IntegrationSharedSettingViewModel
} from "client/Api/IntegrationBuilder/integrationBuilderApps";
import { EMPTY_ARRAY, setTrackingId } from "client/Misc/utils";
import { formatList } from "client/Misc/cultureSpecificUtils";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { useSharedSettingDialog } from "./SharedSettingDialog";
import styles from "./SharedSettingsTab.module.less";

export type SharedSettingsTabProps = {
	appId: number;
	sharedSettings: readonly IntegrationSharedSettingViewModel[];
	isActive: boolean;
	onActionsStateChange(
		actions: readonly ToolbarAction<SharedSettingActionContext>[],
		context: SharedSettingActionContext
	): void;
	onConfirmUnsavedChanges: () => Promise<boolean>;
	onReload: () => void;
};

export const SharedSettingsTab = (props: SharedSettingsTabProps): ReactElement => {
	const {
		appId,
		sharedSettings,
		isActive,
		onActionsStateChange,
		onConfirmUnsavedChanges,
		onReload
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [selectedItems, setSelectedItems] = useState<readonly IntegrationSharedSettingViewModel[]>(EMPTY_ARRAY);
	const [sortingState, setSortingState] = useState<DataSourceSortableState<IntegrationSharedSettingViewModel>>({
		propertyName: "displayName",
		direction: DataSourceSortDirection.Ascending
	});

	const { sortingFor } = useSortHandler<IntegrationSharedSettingViewModel>(sortingState, setSortingState);
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [sharedSettingDialog, openSharedSettingDialog] = useSharedSettingDialog();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const {
		currentItems
	} = useDataSource(
		sharedSettings,
		{
			searchTerm,
			sorting: sortingState
		}
	);

	const {
		multiSelection,
		selectionFor
	} = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems
		}
	);

	const openDialogHandler = useCallback(
		async (item?: IntegrationSharedSettingViewModel): Promise<void> => {
			const isConfirmed: boolean = await onConfirmUnsavedChanges();

			if (!isConfirmed) {
				return;
			}

			const result: TryResult<IntegrationSharedSettingOptionModel> = await openSharedSettingDialog({
				appId,
				initialData: item,
				existingNames: sharedSettings
					.filter(x => x.id !== item?.id)
					.map(x => x.displayName)
			});

			if (!result.wasSuccessful) {
				return;
			}

			setSelectedItems(EMPTY_ARRAY);
			onReload();
		},
		[
			appId,
			onConfirmUnsavedChanges,
			onReload,
			openSharedSettingDialog,
			sharedSettings
		]
	);

	const deleteHandler = useCallback(
		async (items: readonly IntegrationSharedSettingViewModel[]): Promise<void> => {
			if (items.length === 0) {
				return;
			}

			const result = await openConfirmationDialog({
				title: items.length === 1
					? localize("_AreYouSureYouWantToDeleteThisSharedSetting")
					: localize("_AreYouSureYouWantToDeleteTheseSharedSettings"),
				children: items.some(x => x.isUsedByFlows)
					? (
						<span className={styles.usageWarning}>
							<FormattedText
								text={localize("_SharedSettingsInUseAreNotDeletedInfo")}
								thingsToWrap={[
									{
										blockId: "Note",
										wrap: (children) => (
											<b>{children}</b>
										)
									}
								]}
							/>
						</span>
					)
					: undefined,
				confirmText: localize("_Delete"),
				trackingId: "delete-shared-settings-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				const deleteResult: IntegrationSharedSettingDeleteResult = await deleteSharedSettings(
					items.map(x => x.id),
					globalCancellation
				);

				if (deleteResult.kind === IntegrationSharedSettingDeleteResultKind.DeletedSuccessfully) {
					alertManager.showSuccess(localize("_SuccessfullyDeletedTheItems"));
				} else {
					const notDeletedNames: readonly string[] = items.filter(
						x => deleteResult.sharedSettingInUseIds
							.includes(x.id)
					)
						.map(x => x.displayName);

					alertManager.showWarning(
						<FormattedText
							text={localize(
								notDeletedNames.length === items.length
									? "_SharedSettingsUsedByFlowsWereNotDeleted"
									: "_SomeSharedSettingsUsedByFlowsWereNotDeleted"
							)}
							thingsToReplace={[
								{
									what: "{{SharedSettings}}",
									filler: formatList(notDeletedNames)
								}
							]}
						/>
					);
				}

				setSelectedItems(EMPTY_ARRAY);

				setIsBusy(false);
				onReload();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellation,
			onReload,
			openConfirmationDialog
		]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<SharedSettingActionContext> => getMainAction(
			() => openDialogHandler()
		),
		[openDialogHandler]
	);

	const selectionActions = useMemo(
		(): readonly SharedSettingAction[] => getSelectionActions({
			deselectHandler: (): void => setSelectedItems(EMPTY_ARRAY)
		}),
		[]
	);

	const contextualActions = useMemo(
		(): readonly SharedSettingAction[] => listContextualActions({
			edit: openDialogHandler,
			deleteItem: deleteHandler
		}),
		[deleteHandler, openDialogHandler]
	);

	const actionContext = useMemo(
		(): SharedSettingActionContext => ({
			selection: selectedItems,
			isMobile
		}),
		[isMobile, selectedItems]
	);

	useEffect(
		(): (() => void) | void => {
			if (!isActive) {
				return;
			}

			onActionsStateChange(
				buildActionsForTopBar<SharedSettingActionContext, SharedSettingAction>(
					{
						contextualActions,
						selectionActions,
						mainActions: [mainAction]
					},
					actionContext.selection.length !== 0,
					isResponsive,
					false
				),
				actionContext
			);

			return (): void => {
				onActionsStateChange(
					[],
					{
						selection: [],
						isMobile: false
					}
				);
			};
		},
		[
			actionContext,
			contextualActions,
			isActive,
			isResponsive,
			mainAction,
			onActionsStateChange,
			selectionActions
		]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm !== "") {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: localize("_NoSharedSettingsYet"),
				message: localize("_ThereAreNoSharedSettingsYet")
			};
		},
		[searchTerm]
	);

	return (
		<div
			{...setTrackingId("integration-shared-settings")}
			className={styles.root}
		>
			{isBusy && <BusyIndicator />}
			<AlertBar
				isInline
				kind="info"
				isDismissable={false}
				trackingId="info"
			>
				{localize("_IntegrationSettingsInfo")}
			</AlertBar>
			<SearchInputForm
				onChange={setSearchTerm}
				trackingId="search"
				icon="filter"
				height="large"
				placeholder={(
					<SearchPlaceholder
						kind="filter"
						properties={[
							localize("_Value"),
							localize("_DisplayName")
						]}
					/>
				)}
			/>

			<div className={styles.table}>
				<TableView
					multiSelectable={multiSelection}
					items={currentItems}
					itemKey="id"
					height="fill"
					trackingId="table"
					noResultConfig={noResultConfig}
					columns={[
						{
							title: localize("_Value"),
							trackingId: "value",
							width: "fill",
							sortable: sortingFor("value")
						},
						{
							title: localize("_DisplayName"),
							trackingId: "display-name",
							width: "fill",
							sortable: sortingFor("displayName")
						},
						{
							title: localize("_Description"),
							trackingId: "description",
							width: "fill",
							sortable: sortingFor("description")
						}
					]}
				>
					{(item): ReactElement => (
						<TableView.Row
							selectable={selectionFor(item)}
							trackingId={`${item.id}`}
							onExecute={() => openDialogHandler(item)}
						>
							<TableView.Cell>
								{item.value}
							</TableView.Cell>
							<TableView.Cell>
								{item.displayName}
							</TableView.Cell>
							<TableView.Cell>
								{item.description}
							</TableView.Cell>
						</TableView.Row>
					)}
				</TableView>
			</div>
			<div className="preserve-space-for-mobile-plus-button"/>
			{isActive && mainAction.canExecute(actionContext) && (
				<FloatingActionButton
					icon="plus"
					onClick={() => mainAction.execute(actionContext)}
					trackingId="create-shared-settings-fab"
				/>
			)}

			{confirmationDialog}
			{sharedSettingDialog}
		</div>
	);
};
SharedSettingsTab.displayName = "SharedSettingsTab";

export type SharedSettingActionContext = {
	selection: readonly IntegrationSharedSettingViewModel[];
	isMobile: boolean;
};

export type SharedSettingAction = ToolbarAction<SharedSettingActionContext>;

type ActionHandlers = {
	edit: (item: IntegrationSharedSettingViewModel) => void;
	deleteItem: (items: readonly IntegrationSharedSettingViewModel[]) => void;
};


function listContextualActions(
	handlers: ActionHandlers
): readonly SharedSettingAction[] {
	const {
		edit,
		deleteItem
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (c) => c.selection
				.length === 1,
			execute: (c) => edit(c.selection[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.selection
				.length > 0,
			execute: (c) => deleteItem(c.selection),
			icon: "trash-can"
		}
	];
}

function getMainAction(
	createNew: () => void
): ToolbarRegularAction<SharedSettingActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "new",
		title: localize("_New"),
		canExecute: () => true,
		execute: (_) => createNew(),
		icon: "plus",
		isPrimary: true
	};
}

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(
	handlers: SelectionActionsHandlers
): readonly SharedSettingAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: c => c
				.selection
				.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

const useDataSource = createLocalDataSource<IntegrationSharedSettingViewModel>()
	.setConfiguration({
		search: {
			doesMatchItem: searchMatchFunction
		}
	})
	.getHook();

function searchMatchFunction(
	item: IntegrationSharedSettingViewModel,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	return contains(item.value, normalizedNotEmptySearchTerm)
		|| contains(item.displayName, normalizedNotEmptySearchTerm);
}
